/**
 * AURELIA — animations.js
 * Scroll-reveal observer. One IntersectionObserver for the whole document,
 * elements are unobserved as soon as they reveal, and `will-change` is
 * released once the transition settles.
 *
 * Cost: ~1 KB, zero work when animations are disabled or the visitor prefers
 * reduced motion.
 */
(function () {
  'use strict';

  const { motion } = window.Aurelia || {};

  // No JS-driven reveals when motion is reduced — CSS already neutralises the
  // hidden state, so simply doing nothing leaves everything visible.
  if (motion && motion.reduced) return;

  const REVEAL_SELECTOR = '[data-animate], .image-reveal';

  /** Elements above the fold reveal immediately; below-fold ones on scroll. */
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    },
    {
      // Start the reveal slightly before the element enters the viewport so
      // the transition is already underway when it becomes visible.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01,
    }
  );

  function reveal(element) {
    const delay = element.dataset.animateDelay;
    if (delay) element.style.setProperty('--animate-delay', `${delay}ms`);

    element.classList.add('is-revealed');

    // Drop the compositor hint after the transition so long pages do not keep
    // hundreds of promoted layers alive.
    const settle = () => element.classList.add('is-settled');
    const duration = parseFloat(getComputedStyle(element).transitionDuration) * 1000;
    const total = (Number.isFinite(duration) ? duration : 600) + Number(delay || 0) + 80;
    setTimeout(settle, total);
  }

  function observe(root) {
    const scope = root || document;
    const elements =
      scope.matches && scope.matches(REVEAL_SELECTOR)
        ? [scope]
        : Array.from(scope.querySelectorAll(REVEAL_SELECTOR));

    elements.forEach((element) => {
      if (element.classList.contains('is-revealed')) return;

      // Anything already in view on load is revealed without waiting for a
      // scroll event — this is what keeps the LCP element from fading in late.
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        reveal(element);
        return;
      }

      observer.observe(element);
    });
  }

  observe(document);

  // Sections added by the Theme Editor, infinite scroll, or a section re-render
  // must be picked up too.
  if (window.Aurelia) {
    window.Aurelia.on('section:load', ({ target }) => observe(target));
    window.Aurelia.on('content:appended', ({ target }) => observe(target));
  }

  document.addEventListener('shopify:section:load', (event) => observe(event.target));
})();
